import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useState } from 'react';
import { useDownloadQuotesPdfMutation } from 'services/api/insuranceQuoteForm.api';
import { GLASS_BACKGROUND, GLASS_BLUR, NAVY, RADIUS_COIN, SHADOW, YELLOW } from 'styles/tokens';

interface DownloadQuotesDialogProps {
  open: boolean;
  onClose: () => void;
  leadId: number | null;
  filterModel: string;
  sortModel: string;
  totalQuotes: number;
}

const DownloadQuotesDialog: React.FC<DownloadQuotesDialogProps> = ({
  open,
  onClose,
  leadId,
  filterModel,
  sortModel,
  totalQuotes,
}) => {
  const [downloadQuotesPdf, { isLoading, isError, error }] = useDownloadQuotesPdfMutation();
  const [preferredCurrency, setPreferredCurrency] = useState<'UF' | 'CLP'>('UF');
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  const canGenerate = Boolean(leadId) && totalQuotes > 0;
  const isPreview = Boolean(pdfUrl);

  const errorMessage = (() => {
    if (!error || typeof error !== 'object' || !('data' in error)) {
      return 'Error al generar el PDF. Intenta de nuevo.';
    }
    const err = error as { data?: unknown };
    if (typeof err.data === 'string') return err.data;
    return (err.data as { message?: string })?.message ?? 'Error al generar el PDF. Intenta de nuevo.';
  })();

  const handleGenerate = async () => {
    if (!leadId || !canGenerate) return;
    try {
      const result = await downloadQuotesPdf({
        leadId,
        ...(filterModel ? { filterModel } : {}),
        ...(sortModel ? { sortModel } : {}),
        preferredCurrency,
      }).unwrap();
      setPdfUrl(result.url);
    } catch {
      // Error surfaced via isError / error
    }
  };

  const handleDownload = () => {
    if (!pdfUrl) return;
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `cotizaciones-${leadId}.pdf`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClose = () => {
    setPdfUrl(null);
    onClose();
  };

  const glassPaperProps = {
    sx: {
      borderRadius: RADIUS_COIN,
      bgcolor: GLASS_BACKGROUND,
      backdropFilter: GLASS_BLUR,
      boxShadow: SHADOW,
    },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={isPreview ? 'lg' : 'sm'}
      fullWidth
      slotProps={{ paper: glassPaperProps }}
    >
      {isPreview ? (
        <>
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              color: NAVY,
            }}
          >
            Cotizaciones
            <IconButton onClick={handleClose} size="small" sx={{ color: NAVY }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            {pdfUrl && (
              <Box
                component="iframe"
                src={pdfUrl}
                sx={{ width: '100%', height: '80vh', border: 'none' }}
                title="Cotizaciones PDF"
              />
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button
              onClick={handleClose}
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontWeight: 600,
                color: NAVY,
                borderRadius: RADIUS_COIN,
                px: 3,
                '&:hover': { bgcolor: 'rgba(21, 24, 58, 0.04)' },
              }}
            >
              Cerrar
            </Button>
            <Button
              onClick={handleDownload}
              variant="contained"
              disableElevation
              startIcon={<DownloadIcon />}
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontWeight: 600,
                bgcolor: YELLOW,
                color: NAVY,
                borderRadius: RADIUS_COIN,
                px: 3,
                '&:hover': { bgcolor: '#e5c009' },
              }}
            >
              Descargar
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              color: NAVY,
            }}
          >
            Descargar cotizaciones
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1 }}>
              {totalQuotes === 0 ? (
                <Typography
                  sx={{
                    color: 'rgba(21, 24, 58, 0.6)',
                    fontFamily: 'Jost, sans-serif',
                  }}
                >
                  No hay cotizaciones para descargar. Ajusta los filtros o espera a que se generen cotizaciones.
                </Typography>
              ) : (
                <>
                  <Typography sx={{ fontFamily: 'Jost, sans-serif', color: NAVY }}>
                    Se generará un PDF con las cotizaciones que coinciden con los filtros y orden actuales.
                  </Typography>
                  <Typography sx={{ mt: 1, fontFamily: 'Jost, sans-serif', color: NAVY }}>
                    <strong>Total de cotizaciones: </strong>
                    {totalQuotes}
                  </Typography>
                  <FormControl sx={{ mt: 2.5 }}>
                    <Typography
                      variant="body2"
                      gutterBottom
                      sx={{
                        color: 'rgba(21, 24, 58, 0.6)',
                        fontFamily: 'Jost, sans-serif',
                        fontWeight: 600,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        fontSize: '0.75rem',
                      }}
                    >
                      Moneda de las primas en el PDF
                    </Typography>
                    <RadioGroup
                      row
                      value={preferredCurrency}
                      onChange={(e) => setPreferredCurrency(e.target.value as 'UF' | 'CLP')}
                    >
                      <FormControlLabel
                        value="UF"
                        control={<Radio sx={{ color: NAVY, '&.Mui-checked': { color: YELLOW } }} />}
                        label="UF"
                        sx={{ '& .MuiFormControlLabel-label': { fontFamily: 'Jost, sans-serif', color: NAVY } }}
                      />
                      <FormControlLabel
                        value="CLP"
                        control={<Radio sx={{ color: NAVY, '&.Mui-checked': { color: YELLOW } }} />}
                        label="CLP"
                        sx={{ '& .MuiFormControlLabel-label': { fontFamily: 'Jost, sans-serif', color: NAVY } }}
                      />
                    </RadioGroup>
                  </FormControl>
                </>
              )}
              {isError && (
                <Typography color="error" sx={{ mt: 2, fontFamily: 'Jost, sans-serif' }}>
                  {errorMessage}
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button
              onClick={handleClose}
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontWeight: 600,
                color: NAVY,
                borderRadius: RADIUS_COIN,
                px: 3,
                '&:hover': { bgcolor: 'rgba(21, 24, 58, 0.04)' },
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                handleGenerate().catch(() => {});
              }}
              variant="contained"
              disableElevation
              disabled={!canGenerate || isLoading}
              startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontWeight: 600,
                bgcolor: YELLOW,
                color: NAVY,
                borderRadius: RADIUS_COIN,
                px: 3,
                '&:hover': { bgcolor: '#e5c009' },
                '&.Mui-disabled': { bgcolor: 'rgba(21, 24, 58, 0.08)', color: 'rgba(21, 24, 58, 0.38)' },
              }}
            >
              {isLoading ? 'Generando...' : 'Generar PDF'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default DownloadQuotesDialog;
